<template>
  <div>
    <div class="d-flex mb-3">
      <h1 class="h4 primary--text">
        {{ `${$t('views.aftersales_order.order')} ${order.number}` }}
      </h1>
      <div class="ml-auto d-flex">
        <router-link
          to="/aftersales-orders"
          class="aftersales-order__back-link my-auto"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="10"
            viewBox="0 0 10 10"
          >
            <path
              d="M9,4l.881.881L6.394,8.375H14v1.25H6.394l3.487,3.494L9,14,4,9Z"
              transform="translate(-4 -4)"
            />
          </svg>
          {{ $t('views.aftersales_order.back_to_list') }}
        </router-link>
      </div>
    </div>

    <app-loader v-if="loading" />

    <main v-else>
      <v-container
        v-if="workMode.isModeSollers"
        fluid
        class="white border-bottom px-4 aftersales-order__dealer"
      >
        <v-row>
          <v-col>
            <span class="aftersales-order__label">
              {{ $t('views.aftersales_order.dealer_code') }}:
            </span>
            <span>{{ order.dealer.code }} </span>
          </v-col><v-col>
            <span class="aftersales-order__label">
              {{ $t('views.aftersales_order.dealer_name') }}:
            </span>
            <span>{{ order.dealer.name }} </span>
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="order.dealer.employee">
            <span class="aftersales-order__label">
              {{ $t('views.aftersales_order.dealer_employee') }}:
            </span>
            <span>{{ order.dealer.employee }}</span>
          </v-col>
          <v-col>
            <span class="aftersales-order__label">
              {{ $t('views.aftersales_order.dealer_phone') }}:
            </span>
            <span>{{ order.dealer.phone }} </span>
          </v-col><v-col>
            <span class="aftersales-order__label">
              {{ $t('views.aftersales_order.dealer_email') }}:
            </span>
            <span>{{ order.dealer.email }} </span>
          </v-col>
        </v-row>
      </v-container>
      <v-container
        fluid
        class="white border-bottom px-4 aftersales-order__main"
      >
        <v-row>
          <v-col cols="3">
            <span class="aftersales-order__label">
              {{ $t('views.aftersales_order.order_number') }}:
            </span>
            <span>{{ order.number }} </span>
            <span class="aftersales-order__label">
              {{ $t('common.dated') }}
            </span>
            <span>{{ $moment(order.date).format('L') }}</span>
          </v-col>
          <v-col cols="3">
            <span class="aftersales-order__label">
              {{ $t('views.aftersales_order.estimated_date') }}:
            </span>
            <span>{{ $moment(order.estimatedDate).format('L') }}</span>
          </v-col>
          <v-col cols="2">
            <span class="aftersales-order__label">
              {{ $t('views.aftersales_order.total_with_vat') }}:
            </span>
            <span>{{ order.totalWithVAT }}</span>
          </v-col>
          <v-col cols="2">
            <span class="aftersales-order__label">
              {{ $t('common.currency') }}:
            </span>
            <span>{{ order.currency }}</span>
          </v-col>
          <v-col>
            <span class="aftersales-order__label">{{ $t('common.status') }}: </span>
            <span>{{ $t(`enums.aftersales_orders_statuses.${order.status}`) }}</span>
          </v-col>
        </v-row>
      </v-container>

      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="order.parts"
        :show-select="isOrderEditable"
        item-key="_id"
        disable-pagination
        disable-sort
        hide-default-footer
      >
        <template v-slot:item.isConfirmed="{ item }">
          <v-simple-checkbox
            v-model="item.isConfirmed"
            :disabled="!isOrderEditable"
            :ripple="false"
          />
        </template>
        <template
          v-if="editor && isOrderEditable"
          v-slot:item.amount="{ item }"
        >
          <div class="aftersales-order__quantity-wrapper">
            <v-text-field
              v-model.number="item.amount"
              hide-details
              dense
              type="number"
              min="0"
              step="1"
              :max="item.reqAmount"
              :rules="[rules.amountRule(item)]"
              class="
                input-block__input
                input-block__input_white
                input-block__input_table
                aftersales-order__quantity"
              @change="updatePart(item)"
            />
            <v-icon
              class="mr-2 pl-1 aftersales-order__comment"
              :class="item.supplierComment ? '' : 'text--disabled'"
              color="primary"
              size="20"
              @click="showSupplierCommentModal(item)"
            >
              mdi-comment-outline
            </v-icon>
          </div>
        </template><template
          v-if="editor && isOrderEditable"
          v-slot:item.date="{ item }"
        >
          <div
            class="aftersales-order__delivery-date-wrapper"
          >
            <v-tooltip>
              <template v-slot:activator="{ on }">
                <v-icon
                  slot="activator"
                  class="mr-2 text--disabled d-inline"
                  color="primary"
                  size="20"
                  v-on="on"
                >
                  mdi-clock
                </v-icon>
              </template>
              <span>Text {{ item.estimatedDate }}</span>
            </v-tooltip>
            <date-picker
              v-model="item.deliveryTime"
              :rules="[rules.deliveryTimeRule(item)]"
            />
          </div>
        </template>
      </v-data-table>

      <div
        v-if="editor"
        class="mt-8"
      >
        <div class="float-right">
          <v-btn
            v-if="!workMode.isModeSollers"
            :disabled="disableIssueAsn"
            color="primary"
            outlined
            large
            class="btn mr-4"
            @click="$router.push(`/asn/create?order=${order.GUID}`)"
          >
            <v-icon left>
              mdi-file-plus
            </v-icon>
            {{ $t('views.aftersales_order.issue_asn') }}
          </v-btn>
          <v-btn
            :disabled="disableConfirmOrder"
            color="primary"
            outlined
            large
            class="btn mr-4"
            @click="confirm"
          >
            <v-icon left>
              mdi-file-check
            </v-icon>
            {{ $t('common.confirm') }}
          </v-btn>
          <v-btn
            v-if="!workMode.isModeSollers"
            :disabled="disableShipToDealer"
            color="primary"
            outlined
            large
            class="btn mr-4"
            @click="closeOrder"
          >
            <v-icon left>
              mdi-truck
            </v-icon>
            {{ $t('views.aftersales_order.shipped_to_dealer') }}
          </v-btn>
        </div>
      </div>
    </main>
    <div
      v-if="!workMode.isModeSollers"
      class="aftersales-order-asn-list"
    >
      <shipment-notice-list
        v-if="asnFilter.enabled"
        :static-filter="asnFilter"
      />
    </div>
    <b-modal
      v-if="isSupplierCommentShow"
      ref="modal"
      v-model="isSupplierCommentShow"
      :title="$t('views.aftersales_order.supplier_comment')"
      modal-class="spso-modal supplier-comment-modal"
      hide-header-close
      hide-footer
      centered
      size="lg"
    >
      <b-col>
        <v-textarea
          v-model="partSupplierComment"
          name="input-7-1"
          variant="filled"
          label="Comment"
          auto-grow
        />
      </b-col>
      <main class="shipment-notice__main">
        <div class="v-application d-flex mt-4">
          <v-btn
            color="primary"
            class="mr-4 flex-grow-1"
            outlined
            large
            @click="hideSupplerCommentModal(true)"
          >
            <v-icon
              left
            >
              mdi-plus
            </v-icon>
            {{ $t('common.add') }}
          </v-btn>
          <v-btn
            class="flex-grow-1"
            outlined
            large
            @click="hideSupplerCommentModal(false)"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </div>
      </main>
    </b-modal>
  </div>
</template>

<script>
import _round from 'lodash.round';
import { mapState, mapGetters } from 'vuex';
import AppLoader from '@/components/common/AppLoader.vue';
import ShipmentNoticeList from '@/views/asn/ShipmentNoticeList.vue';
import DatePicker from '@/components/common/DatePicker.vue';

export default {
  name: 'AftersalesOrder',

  components: {
    AppLoader,
    DatePicker,
    ShipmentNoticeList,
  },

  props: {
    number: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      order: {},
      anyAsn: false,
      anyRemainPart: false,
      remainParts: {},
      selected: [],
      loading: false,
      processing: false,
      isSupplierCommentShow: false,
      itemSupplierComment: null,
      partSupplierComment: '',

      commonHeaders: [
        { text: this.$t('common.part_number'), value: 'partNumber' },
        { text: this.$t('common.part_name'), value: 'partName' },
        { text: this.$t('views.aftersales_order.amount'), value: 'amount' },
        { text: this.$t('views.aftersales_order.uom'), value: 'UOM' },
        { text: this.$t('views.aftersales_order.deliveryTime'), value: 'date' },
        { text: this.$t('common.price'), value: 'price' },
        { text: this.$t('views.aftersales_order.sum'), value: 'sum' },
        { text: this.$t('views.aftersales_order.vat_rate'), value: 'VATRate' },
        { text: this.$t('views.aftersales_order.vat'), value: 'VAT' },
        { text: this.$t('views.aftersales_order.sum_with_vat'), value: 'sumWithVAT' },
      ],

      OrderStatuses: {
        ERP_AS_APPROVED: 'ERP_AS_APPROVED',
        ERP_AS_ON_APPROVAL: 'ERP_AS_ON_APPROVAL',
        ERP_AS_CONFIRMED: 'ERP_AS_CONFIRMED',
        ERP_AS_READY_TO_SHIP: 'ERP_AS_READY_TO_SHIP',
        ERP_AS_CLOSED: 'ERP_AS_CLOSED',
        ERP_AS_CANCELED: 'ERP_AS_CANCELED',
      },

      asnFilter: { enabled: false, filter: {} },

      rules: {
        amountRule(item) {
          if (item.amount < 0) { return false; }
          if (item.amount > item.reqAmount) { return false; }

          return (Math.round(item.amount) === item.amount);
        },
        deliveryTimeRule(item) {
          if (!item.deliveryTime) { return false; }
          const d = new Date(item.deliveryTime);

          return (d > new Date());
        },
      },
    };
  },

  computed: {
    ...mapGetters(['editor']),
    ...mapState(['workMode']),

    headers() {
      if (this.isOrderEditable) {
        return this.commonHeaders;
      }
      return [
        { text: this.$t('views.aftersales_order.is_confirmed'), value: 'isConfirmed' },
        ...this.commonHeaders,
      ];
    },
    isOrderEditable() {
      if (this.workMode.isModeSollers) {
        return !this.orderHasStatus([
          this.OrderStatuses.ERP_AS_CLOSED,
          this.OrderStatuses.ERP_AS_CANCELED,
        ]);
      }
      return this.orderHasStatus(this.OrderStatuses.ERP_AS_APPROVED);
    },
    disableConfirmOrder() {
      if (this.processing || !this.confirmedPartsCount) return true;

      return !this.isOrderEditable;
    },
    disableIssueAsn() {
      return !this.orderHasStatus(this.OrderStatuses.ERP_AS_READY_TO_SHIP)
              || !this.anyRemainPart
              || this.processing;
    },
    disableShipToDealer() {
      return !this.orderHasStatus(this.OrderStatuses.ERP_AS_READY_TO_SHIP)
              || this.anyAsn
              || this.processing;
    },
    confirmedPartsCount() {
      return (this.order.parts.filter((part) => part.isConfirmed)).length;
    },
    disableConfirmCheckbox() {
      return this.processing
                || !this.orderHasStatus(this.OrderStatuses.ERP_AS_APPROVED);
    },
  },

  watch: {
    selected(val) {
      this.order.parts.forEach((part) => {
        // eslint-disable-next-line no-underscore-dangle
        part.isConfirmed = !!(val.find((item) => item._id === part._id));
      });
    },
  },
  created() {
    this.getOrder();
  },

  methods: {
    getAmountViolationMessage(item) {
      if (item.amount < 0) { return this.$t('views.aftersales_order.confirm_amount_negative', { amount: item.reqAmount, confirmed: item.amount }); }
      if (item.amount > item.reqAmount) { return this.$t('views.aftersales_order.confirm_amount_too_much', { amount: item.reqAmount, confirmed: item.amount }); }

      if (Math.round(item.amount) !== item.amount) { return this.$t('views.aftersales_order.confirm_amount_fraction', { amount: item.reqAmount, confirmed: item.amount }); }

      return this.$t('views.aftersales_order.confirm_amount_incorrect', { amount: item.reqAmount, confirmed: item.amount });
    },

    getDatesViolationMessage(item) {
      if (!item.deliveryTime) { return this.$t('views.aftersales_order.confirm_delivery_time_not_set'); }
      if (new Date(item.deliveryTime) < new Date()) { return this.$t('views.aftersales_order.confirm_delivery_time_in_pasta', { date: item.deliveryTime }); }

      return this.$t('views.aftersales_order.confirm_amount_incorrect', { date: item.deliveryTime });
    },

    orderHasStatus(status) {
      const statusList = Array.isArray(status) ? status : [status];

      return statusList.includes(this.order.status);
    },
    async getOrder() {
      this.loading = true;
      try {
        const requests = [
          this.$http.get(`/aftersales-orders/${this.number}`),
          (this.workMode.isModeSollers ? {}
            : this.$http.get(`/aftersales-orders/${this.number}/asn-parts`)),
        ];

        const [{ data = {} }, { data: partData = {} }] = await Promise.all(requests);

        this.order = {
          dealer: {},
          ...data,
        };

        this.order.parts.forEach((item) => {
          if (!item.reqAmount) { item.reqAmount = item.amount; }
          if (item.estimatedDate && !item.deliveryTime) { item.deliveryTime = item.estimatedDate; }
        });

        this.selected = this.order.parts.filter((part) => part.isConfirmed);
        if (!this.workMode.isModeSollers) {
          this.asnFilter = {
            enabled: true,
            filter: {
              supplier: this.order.supplier,
              buyer: this.order.shipto,
              isAfterSales: true,
              asOrderGuid: this.order.GUID,
            },
          };

          this.anyAsn = partData.anyAsn;
          this.remainParts = partData.remainParts;
          this.anyRemainPart = Object.values(this.remainParts).find((val) => val > 0);
        }
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loading = false;
      }
    },

    updatePart(item) {
      item.sum = _round(item.amount * item.price, 2);
      item.VAT = _round(item.sum * (item.VATRate / 100), 2);
      item.sumWithVAT = _round(item.sum + item.VAT, 2);
      this.order.totalWithVAT = _round(
        this.order.parts.reduce((acc, it) => acc + it.sumWithVAT, 0),
        2,
      );
    },

    showSupplierCommentModal(item) {
      this.itemSupplierComment = item;
      this.partSupplierComment = item.supplierComment || '';
      this.isSupplierCommentShow = true;
    },

    hideSupplerCommentModal(update) {
      if (update) {
        this.itemSupplierComment.supplierComment = this.partSupplierComment || '';
      }
      this.isSupplierCommentShow = false;
    },

    validateConfirmedParts() {
      const confirmedParts = this.order.parts
        .filter((item) => item.isConfirmed);
      const checkAmount = confirmedParts
        .find((item) => !this.rules.amountRule(item));

      const checkDates = confirmedParts
        .find((item) => !this.rules.deliveryTimeRule(item));

      if (checkAmount) {
        this.$toast(this.getAmountViolationMessage(checkAmount), {
          title: this.$t('toast.danger'),
          variant: 'danger',
        });
        return false;
      }

      if (checkDates) {
        this.$toast(this.getDatesViolationMessage(checkDates), {
          title: this.$t('toast.danger'),
          variant: 'danger',
        });
        return false;
      }

      return true;
    },

    async confirm() {
      this.processing = true;

      const confirmedParts = this.order.parts.filter((item) => item.isConfirmed);

      if (!this.validateConfirmedParts(confirmedParts)) {
        this.processing = false;
        return;
      }

      const parts = confirmedParts.map((item) => ({
        // eslint-disable-next-line no-underscore-dangle
        _id: item._id,
        amount: item.amount,
        isConfirmed: item.isConfirmed,
        supplierComment: item.supplierComment,
      }));

      try {
        await this.$http.patch(`/aftersales-orders/${this.number}`, { parts });
        this.$router.push('/aftersales-orders');
      } finally {
        this.processing = false;
      }
    },

    async closeOrder() {
      this.processing = true;
      try {
        await this.$http.patch(`/aftersales-orders/${this.number}/${this.OrderStatuses.ERP_AS_CLOSED}`);
        this.$router.push('/aftersales-orders');
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.aftersales-order-asn-list {
  margin-top: 50px;
}

.aftersales-order__back-link {
  text-decoration: none;
  transition: color 0.3s ease-in-out 0s;

  svg {
    fill: #ad7c59;
    margin-right: 5px;
    transition: fill 0.3s ease-in-out 0s;
  }

  &:hover {
    color: var(--margaritas);

    svg {
      fill: var(--margaritas);
    }
  }
}
.aftersales-order {
  &__main {
    max-width: 100%;
  }

  &__dealer {
    max-width: 100%;
    margin: 20px 0px 20px 0px;
    padding-top: 20px;
    padding-bottom: 20px;
  }

  &__quantity {
    max-width: 80px;
    float:left;
  }

  &__quantity-wrapper {
    display: inline;
  }

  &-wrapper {
    display: flex;
  }

  &__label {
    color: #afafaf;
  }

  &__comment {
    margin-top: 10px;
  }

  &__shipped {
    float: right;
    color: var(--aurum);

    &:hover {
      color: var(--margaritas)
    }
  }
}
</style>
